import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiClient, apiV1 } from '@/api/client';

const AuthContext = createContext(null);

/**
 * AuthContext — cookie-first identity for Alchemy SSO.
 *
 * Resolution order on mount:
 *   1) `GET /api/auth/whoami` (HttpOnly cookie session set by the Alchemy
 *      callback). If 200 → we're signed in, done.
 *   2) Legacy Bearer fallback (`GET /api/v1/auth/me`) for break-glass
 *      admins who logged in via the email/password form. Only attempted
 *      when a legacy token sits in localStorage.
 *   3) Otherwise: unauthenticated.
 *
 * Sign-in path:
 *   - SSO users go through `GET /api/auth/login-info` → window.location to
 *     Alchemy. They never call `login()`.
 *   - Break-glass admins still call `login(email, password)` against the
 *     legacy endpoint — preserved here for the disclosure path on /login.
 *
 * Sign-out path:
 *   - Always POST `/api/auth/session/logout` (idempotent: 200 even if no
 *     cookie session existed).
 *   - Plus the legacy `/api/v1/auth/logout` when we hold a refresh token.
 */

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshToken, setRefreshToken] = useState(null);

  // Initialize: try cookie session first, then legacy Bearer.
  //
  // Teardown-safe: every browser API access (localStorage, state setters)
  // is gated on `cancelled`. In jsdom, the window is torn down at end of
  // test, and any access to `localStorage` after that throws "Cannot read
  // properties of null (reading '_origin')" — which would tank unrelated
  // test suites whose AuthProvider init is still pending when they unmount.
  useEffect(() => {
    let cancelled = false;

    // Wrap localStorage in try/catch so a torn-down jsdom window never
    // crashes the worker. Returns null on failure — same as a missing key.
    const safeGetItem = (key) => {
      if (cancelled) return null;
      try {
        return localStorage.getItem(key);
      } catch {
        return null;
      }
    };

    const initAuth = async () => {
      // 1) Cookie session via Alchemy whoami. The endpoint returns the User
      // model directly (no `{success, data}` envelope), so we shape-check the
      // response before treating it as authoritative — defends against
      // misconfigured test mocks and against accidentally hitting a route
      // that doesn't exist (which would return HTML).
      try {
        const resp = await apiClient.get('/auth/whoami');
        if (cancelled) return;
        const body = resp?.data;
        const looksLikeUser =
          body && typeof body === 'object' && body.id && (body.email || body.role);
        if (looksLikeUser) {
          setUser(body);
          setLoading(false);
          return;
        }
      } catch (err) {
        if (cancelled) return;
        // 401 from whoami is the no-cookie case — fall through to legacy.
        if (err?.status && err.status !== 401) {
          // Network error or unexpected — surface as unauth and stop.
          clearAuth();
          setLoading(false);
          return;
        }
      }

      // 2) Legacy Bearer break-glass path.
      if (cancelled) return;
      const token = safeGetItem('token');
      const storedUser = safeGetItem('user');
      const storedRefreshToken = safeGetItem('refreshToken');

      if (token && storedUser) {
        try {
          const response = await apiV1.get('/auth/me');
          if (cancelled) return;
          if (response.data?.success) {
            setUser(response.data.data);
            setRefreshToken(storedRefreshToken);
          } else {
            clearAuth();
          }
        } catch (error) {
          // Interceptor may have refreshed — re-hydrate from storage.
          if (cancelled) return;
          const currentToken = safeGetItem('token');
          if (currentToken) {
            try {
              const raw = safeGetItem('user');
              if (raw) setUser(JSON.parse(raw));
              setRefreshToken(safeGetItem('refreshToken'));
            } catch (e) {
              clearAuth();
            }
          } else {
            clearAuth();
          }
        }
      }

      if (!cancelled) setLoading(false);
    };

    initAuth();
    return () => {
      cancelled = true;
    };
  }, []);

  const clearAuth = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    setUser(null);
    setRefreshToken(null);
  };

  // Legacy break-glass refresh — only used when a Bearer token was issued.
  const refreshAccessToken = async (token) => {
    try {
      const response = await apiV1.post('/auth/refresh', {
        refresh_token: token,
      });

      if (response.data?.success) {
        const { access_token, refresh_token: newRefreshToken } = response.data.data;
        localStorage.setItem('token', access_token);
        localStorage.setItem('refreshToken', newRefreshToken);
        setRefreshToken(newRefreshToken);

        const userResponse = await apiV1.get('/auth/me');
        if (userResponse.data?.success) {
          setUser(userResponse.data.data);
          localStorage.setItem('user', JSON.stringify(userResponse.data.data));
        }
      }
    } catch (error) {
      clearAuth();
      throw error;
    }
  };

  /**
   * Break-glass local login. SSO users get rejected here with
   * `code=USE_SSO` (Phase A backend change in /api/v1/auth/login).
   * Returns the same shape as before so existing callers keep working.
   */
  const login = async (email, password, twoFactorCode = null) => {
    const payload = { email, password };
    if (twoFactorCode) {
      payload.two_factor_code = twoFactorCode;
    }

    const response = await apiV1.post('/auth/login', payload);

    if (response.data?.success === false && response.data?.data?.requires_2fa) {
      return { requires_2fa: true };
    }

    if (response.data?.success) {
      const { access_token, refresh_token, user: userData } = response.data.data;

      localStorage.setItem('token', access_token);
      localStorage.setItem('refreshToken', refresh_token);
      localStorage.setItem('user', JSON.stringify(userData));

      setUser(userData);
      setRefreshToken(refresh_token);

      return userData;
    }

    throw new Error(response.data?.message || 'Login failed');
  };

  /**
   * Kick off the Alchemy SSO flow. Fetches the configured login URL from
   * the backend (so we don't ship the GUID in the SPA bundle) and hands
   * the browser off. Returns false on failure so the caller can fall
   * back to the break-glass form.
   */
  const loginViaAlchemy = async () => {
    try {
      const resp = await apiClient.get('/auth/login-info');
      const loginUrl = resp?.data?.loginUrl;
      if (loginUrl) {
        // Preserve the current path so the callback can land the user back here.
        const here = window.location.pathname + window.location.search;
        if (here && here !== '/login' && here !== '/') {
          localStorage.setItem('redirectAfterLogin', here);
        }
        window.location.href = loginUrl;
        return true;
      }
    } catch (err) {
      console.error('Failed to start Alchemy SSO:', err);
    }
    return false;
  };

  const logout = async () => {
    // Always clear the cookie session — idempotent on the server.
    try {
      await apiClient.post('/auth/session/logout');
    } catch (error) {
      console.warn('Cookie session logout error:', error);
    }

    // Legacy refresh-token revoke for break-glass sessions.
    try {
      const currentRefreshToken = localStorage.getItem('refreshToken');
      if (currentRefreshToken) {
        await apiV1.post('/auth/logout', { refresh_token: currentRefreshToken });
      }
    } catch (error) {
      console.warn('Legacy logout error:', error);
    } finally {
      clearAuth();
    }
  };

  const hasPermission = useCallback(
    (permission) => {
      if (!user) return false;
      return user.permissions?.includes(permission) || user.role === 'admin';
    },
    [user]
  );

  /**
   * Re-fetch the current user from /api/auth/whoami. Used after server-side
   * state changes that the user object reflects — e.g. completing the
   * first-run onboarding tour stamps `onboarded_at` server-side, and the
   * SPA wants the updated flag without a full page reload.
   *
   * Same shape-check as the initial probe — defends against misconfigured
   * mocks and route surprises.
   */
  const refreshUser = useCallback(async () => {
    try {
      const resp = await apiClient.get('/auth/whoami');
      const body = resp?.data;
      const looksLikeUser =
        body && typeof body === 'object' && body.id && (body.email || body.role);
      if (looksLikeUser) {
        setUser(body);
        return body;
      }
    } catch (err) {
      // 401 means the session expired between calls — let the interceptor
      // handle the redirect. Other errors fall through silently; the caller
      // can retry.
      if (err?.status && err.status !== 401) {
        console.warn('refreshUser failed:', err);
      }
    }
    return null;
  }, []);

  const changePassword = async (currentPassword, newPassword) => {
    const response = await apiV1.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });

    if (response.data?.success) {
      // After password change, all tokens are revoked - user needs to re-login.
      clearAuth();
      return true;
    }

    throw new Error(response.data?.message || 'Password change failed');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        loginViaAlchemy,
        logout,
        hasPermission,
        changePassword,
        refreshToken,
        refreshUser,
        // Surface auth method so UI can tailor sign-out / settings affordances.
        authMethod: user?.auth_method || 'local',
        isAlchemyUser: user?.auth_method === 'alchemy',
        isPartner: user?.auth_method === 'partner',
        // First-run onboarding flag. `onboarded === false` is the signal to
        // show welcome content / a tour. Becomes true after the SPA hits
        // POST /api/auth/onboarding/complete and calls refreshUser().
        onboarded: user?.onboarded === true,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
